import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import {
  fetchClient,
  fetchClients,
  setClientApiSecret,
} from "../../Apis/Wallet-api";
import WalletPageShell from "../../Components/Wallet/WalletPageShell";
import { formatLabel } from "../../Utils/walletFormat";
import { hasPermissionAction } from "../../Utils/organizationPermissions";
import {
  isWalletAgentUser,
  isWalletClientUser,
  isWalletScopedUser,
  resolveWalletClientId,
} from "../../Utils/walletAdminAccess";

const CLIENT_API_BASE = `${MAIN_URL}/api/client`;

function CredRow({ label, value, onCopy }) {
  if (value == null || value === "") return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography
          variant="body2"
          fontFamily="monospace"
          sx={{ wordBreak: "break-all", flex: 1 }}
        >
          {String(value)}
        </Typography>
        <Button size="small" onClick={() => onCopy(label, value)}>
          Copy
        </Button>
      </Stack>
    </Box>
  );
}

export default function IntegrationPage() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const isClientUser = isWalletClientUser(userData);
  const isAgentUser = isWalletAgentUser(userData);
  const isScopedUser = isWalletScopedUser(userData);
  const canManageIntegration = hasPermissionAction(
    Permission,
    "WALLET_INTEGRATION_MANAGE||WALLET_ADMIN_ALL"
  );

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [client, setClient] = useState(null);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingClient, setLoadingClient] = useState(false);

  const [secretDialog, setSecretDialog] = useState({
    open: false,
    mode: "generate",
    secret: "",
    confirm: "",
  });
  const [secretResult, setSecretResult] = useState(null);
  const [secretSaving, setSecretSaving] = useState(false);

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const rows = await fetchClients();
      const list = Array.isArray(rows) ? rows : [];
      setClients(list);

      const fromUser = resolveWalletClientId(userData);
      if (fromUser) {
        setClientId(String(fromUser));
      } else if (list.length === 1) {
        setClientId(String(list[0].client_id));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load clients.");
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, [userData]);

  const loadClient = useCallback(async () => {
    const id = Number(clientId);
    if (!id) {
      setClient(null);
      return;
    }
    setLoadingClient(true);
    try {
      setClient(await fetchClient(id));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load credentials.");
      setClient(null);
    } finally {
      setLoadingClient(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const copyText = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  const submitSecret = async () => {
    const id = Number(clientId);
    if (!id) return;

    if (secretDialog.mode === "custom") {
      if (!secretDialog.secret || secretDialog.secret.length < 16) {
        toast.error("Secret must be at least 16 characters.");
        return;
      }
      if (secretDialog.secret !== secretDialog.confirm) {
        toast.error("Secret confirmation does not match.");
        return;
      }
    }

    setSecretSaving(true);
    try {
      const result = await setClientApiSecret(
        id,
        secretDialog.mode === "custom" ? secretDialog.secret : undefined
      );
      setSecretResult(result);
      toast.success(result.rotated ? "API secret rotated." : "API secret set.");
      await loadClient();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to set secret.");
    } finally {
      setSecretSaving(false);
    }
  };

  const loading = loadingClients || loadingClient;
  const apps = client?.external_applications || [];
  const ips = client?.allowed_ips || [];

  return (
    <WalletPageShell
      heading="Integration"
      subtitle={
        isAgentUser
          ? "Your agent and client API credentials for system integrations"
          : isClientUser
            ? "Your client and agent API credentials for system integrations"
            : "Client and agent API credentials for external integrations"
      }
    >
      {loadingClients ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : clients.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No wallet client linked to your account yet.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {clients.length > 1 && !isScopedUser && (
            <FormControl size="small" sx={{ maxWidth: 360 }}>
              <InputLabel>Client</InputLabel>
              <Select
                label="Client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                {clients.map((c) => (
                  <MenuItem key={c.client_id} value={String(c.client_id)}>
                    {c.client_name} (#{c.client_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {loading && !client ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !client ? (
            <Typography color="text.secondary">Select a client to view credentials.</Typography>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Client API (system integration)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use these headers on <code>{CLIENT_API_BASE}/*</code>:
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Base URL
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontFamily="monospace">
                        {CLIENT_API_BASE}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => copyText("Base URL", CLIENT_API_BASE)}
                      >
                        Copy
                      </Button>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      API key (<code>X-Api-Key</code>)
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        sx={{ wordBreak: "break-all", flex: 1 }}
                      >
                        {client.api_key || "—"}
                      </Typography>
                      {client.api_key && (
                        <Button
                          size="small"
                          onClick={() => copyText("API key", client.api_key)}
                        >
                          Copy
                        </Button>
                      )}
                    </Stack>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    API secret (<code>X-Api-Secret</code>) —{" "}
                    {client.has_api_secret ? "set" : "not set"} · shown only when
                    issued or rotated
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canManageIntegration}
                  onClick={() => {
                    setSecretResult(null);
                    setSecretDialog({
                      open: true,
                      mode: "generate",
                      secret: "",
                      confirm: "",
                    });
                  }}
                >
                  {client.has_api_secret || client.api_key
                    ? "Rotate API secret"
                    : "Issue credentials"}
                </Button>
                {!canManageIntegration && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    You can view credentials but not rotate them without integration manage permission.
                  </Typography>
                )}
              </Paper>

              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">Agent API credentials</Typography>
                  <Chip size="small" label={`${apps.length} agent(s)`} />
                </Stack>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Agent wallet APIs use each agent&apos;s own key/secret on{" "}
                  <code>/api/agent/*</code>. Open an agent to rotate its secret or
                  transaction password.
                </Alert>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Agent</TableCell>
                        <TableCell>Application user ID</TableCell>
                        <TableCell>API key</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {apps.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary">
                              No agents yet. Create an agent to get wallet API credentials.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        apps.map((app) => (
                          <TableRow key={app.id}>
                            <TableCell>{app.name}</TableCell>
                            <TableCell>
                              {app.application_user_id != null
                                ? app.application_user_id
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                  sx={{ wordBreak: "break-all" }}
                                >
                                  {app.api_key || "—"}
                                </Typography>
                                {app.api_key && (
                                  <Button
                                    size="small"
                                    onClick={() => copyText("API key", app.api_key)}
                                  >
                                    Copy
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={formatLabel(app.status || "unknown")}
                                color={app.status === "active" ? "success" : "default"}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {app.wallet_id ? (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    navigate(`/wallet/applications/${app.wallet_id}`, {
                                      state: {
                                        appName: app.name,
                                        externalAppId: app.id,
                                      },
                                    })
                                  }
                                >
                                  Manage
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Allowed IPs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Shared by client and agent API calls for this tenant.
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>IP / CIDR</TableCell>
                        <TableCell>Label</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ips.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="body2" color="text.secondary">
                              No IPs configured — API access is open when allowlist is
                              empty.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        ips.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Typography fontFamily="monospace" variant="body2">
                                {row.ip_address}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.label || "—"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Stack>
      )}

      <Dialog
        open={secretDialog.open}
        onClose={() => {
          if (!secretResult) {
            setSecretDialog({
              open: false,
              mode: "generate",
              secret: "",
              confirm: "",
            });
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {secretResult ? "API secret ready" : "Set / rotate client API secret"}
        </DialogTitle>
        <DialogContent>
          {secretResult ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                Copy the API secret now — it is shown only once.
              </Alert>
              <CredRow
                label="API key"
                value={secretResult.api_key}
                onCopy={copyText}
              />
              <CredRow
                label="API secret"
                value={secretResult.api_secret}
                onCopy={copyText}
              />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <RadioGroup
                value={secretDialog.mode}
                onChange={(e) =>
                  setSecretDialog((d) => ({ ...d, mode: e.target.value }))
                }
              >
                <FormControlLabel
                  value="generate"
                  control={<Radio size="small" />}
                  label="Generate a new secret (recommended)"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" />}
                  label="Set my own secret (min 16 characters)"
                />
              </RadioGroup>
              {secretDialog.mode === "custom" && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="API secret"
                    value={secretDialog.secret}
                    onChange={(e) =>
                      setSecretDialog((d) => ({ ...d, secret: e.target.value }))
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Confirm secret"
                    value={secretDialog.confirm}
                    onChange={(e) =>
                      setSecretDialog((d) => ({ ...d, confirm: e.target.value }))
                    }
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {secretResult ? (
            <Button
              variant="contained"
              onClick={() => {
                setSecretResult(null);
                setSecretDialog({
                  open: false,
                  mode: "generate",
                  secret: "",
                  confirm: "",
                });
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                onClick={() =>
                  setSecretDialog({
                    open: false,
                    mode: "generate",
                    secret: "",
                    confirm: "",
                  })
                }
              >
                Cancel
              </Button>
              <Button variant="contained" disabled={secretSaving} onClick={submitSecret}>
                {secretSaving ? "Saving…" : "Confirm"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </WalletPageShell>
  );
}
